import _ from 'underscore';
import ko from 'knockout';
import { KoSchemaString } from './string/index';
import { Rules } from './rules';
import { Extension } from './extension';
import { DefaultValue } from './default-value';
import { CustomConstraints } from './custom-constraints';
import './ko-validate';

/**
 * define Object Validator
 * @typedef {Object} Validator
 * @property {function} isValid - validate callback function, will call it when validate
 * @property {string} message - when validate failed, show this message
 */

/**
 * define Object CustomValidators
 * @typedef {Object.<string, Validator>} CustomValidators
 * @property {string} - property name of validator
 * @property {Validator} - validator of this property
 */

/**
 * define Object Rules
 * @typedef {Object.<string, Validator>} Rules
 * @property {string} - rule type of the validator
 * @property {Validator} - validator of this type
 */

/**
 * define Object knockout view model
 * @typedef {Object.<string, Observable>} KnockoutViewModel
 */

/**
 * define Object I18n
 * @typedef {Object} I18n
 * @property {function} getString - get a i18n string with key and params
 */


/**
 * Get Observables, return a object contains all properties define
 * @param {JsonSchema} schema - an object which contains property 'properties',
 * the 'properties' is an Object contains properties and correspond define
 * @param {string[]} names - an array of string, each item is name of property will generate
 * @param {Object} model - the model to initialize the view model
 * @param {CustomValidators} customValidators - allow you pass custom validator for property
 * @param {I18n} i18n - used to get value for each validator
 * @returns {Object} - return Object includes all properties observable define
 */
function getObservables(schema, names, model, customValidators, i18n) {
  const ret = {};
  const keys = _.intersection(_.keys(schema.properties), names);

  _.each(keys, (key) => {
    const prop = schema.properties[key];
    const rules = Rules(prop, schema, key, customValidators, i18n);
    const value = _.result(model, key, DefaultValue(prop));

    // already moved out to component/ko-schema-string
    if (prop.type === 'string' && !prop.enum) {
      ret[key] = KoSchemaString(prop, value, rules, i18n);

      if (ret[key].validators.maxLength) {
        ret[key].validators.maxLength.set({
          blockInput: false,
        });
      }

      ret[key].char_rem = ret[key].strRemaining;
    } else {
      if (prop.type === 'array') {
        ret[key] = ko.observableArray(value);
      } else {
        ret[key] = ko.observable(value);
      }

      ret[key] = ret[key].extend({ validate: _.values(rules) });
      ret[key].validators = rules;

      Extension(ret[key], prop, i18n);
    }

    // only take nullable attributes from override list,
    // as generated nullable attribute is not accurate
    if (prop.nullable || _.contains(schema.nullable, key)) {
      _.invoke(ret[key].validators, 'set', { nullable: true });
    }
  });

  return ret;
}

/**
 * convert koModel to general object
 * @param {KnockoutViewModel} koModel - Object of properties
 * @param {string[]} keys - list of property contained in returned object
 * @returns {Object} - return object include property in keys and correspond value
 */
function toObject(koModel, keys) {
  return _.object(keys, _.map(keys, key => koModel[key]()));
}

/**
 * set value to koModel for targetKeys
 * @param {KnockoutViewModel} koModel - Object of properties
 * @param {string[]} keys - keys defined in this module
 * @param {Object} newModel - the model to set the view model
 * @param {string[]} targetKeys - list of property to set
 * @returns {undefined} - has set value in passed koModel
 */
function setModel(koModel, keys, newModel, targetKeys) {
  const setKeys = !_.isEmpty(targetKeys) ? _.intersection(keys, targetKeys) : keys;

  _.each(setKeys, (key) => {
    koModel[key](_.result(newModel, key));
  });
}

/**
 * validate if all property in keys is validate
 * @param {KnockoutViewModel} koModel - Object of properties
 * @param {string[]} keys - list of property will be validated
 * @returns {boolean} - return true if passed all validate, otherwise, return false
 */
function validate(koModel, keys) {
  return _.chain(keys)
    .map(key => !_.isFunction(koModel[key].validate) || koModel[key].validate())
    .every()
    .value();
}

/**
 * clear global errors in koModel and poperty errors
 * @param {KnockoutViewModel} koModel - Object of properties
 * @param {string[]} keys - list of property to clear erros
 * @param {function} predicate - the predicate function of remove error,
 * default is remove all errors
 * @returns {undefined} - remove errors in koModel
 */
function clearErrors(koModel, keys, predicate = _.constant(true)) {
  koModel.errors.remove(predicate);
  _.each(keys, (key) => {
    koModel[key].errors.remove(predicate);
  });
}

/**
 * seperate errors to correspond property
 * @param {KnockoutViewModel} koModel - Object of properties
 * @param {string[]} keys - list of property defined in this module
 * @param {I18n} i18n - will be called getString with key 'KoSchema_GenericErrorMessage'
 * @param {Object[]} errors - list of array response from server,
 * each item has property 'property' means property name
 * @returns {undefined} - add errors to correspond property in koModel
 */
function addErrors(koModel, keys, i18n, errors) {
  clearErrors(koModel, keys);

  if (errors) {
    _.chain(errors)
      .groupBy('property')
      .each((propertyErrors, property) => {
        const uniqErrors = _.uniq(propertyErrors, false, error => error.toString());

        if (_.has(koModel, property)) {
          koModel[property].errors(uniqErrors);
        } else {
          ko.observableArray.fn.push.apply(koModel.errors, uniqErrors);
        }
      });
  } else {
    koModel.errors([i18n.getString('KoSchema_GenericErrorMessage')]);
  }
}

/**
 * check whether the value of property has been changed after initialized
 * @param {KnockoutViewModel} koModel - Object of properties, each property value is observable
 * @param {Object} originalModel - the model to initialize the view model
 * @param {string[]} keys - list of property want to check
 * @returns {boolean} - if has changed return true, otherwise, return false
 */
function hasChange(koModel, originalModel, keys) {
  const current = koModel.toObject();
  const original = _.pick(originalModel, keys);

  return !_.isEqual(current, original);
}

/**
 * define property and extend property of returnd model
 * @param {KnockoutViewModel} koModel - Object to extend in this module
 * @param {JsonSchema} schema - an object which contains property 'properties',
 * the 'properties' is an Object contains properties and correspond define
 * @param {string[]} keys - list of peroerty to extend in this module
 * @param {Object} model - the model to initialize the view model
 * @param {CustomerValidators} customValidators - custom validators for each property
 * @param {I18n} i18n - used to get value for each validator
 * @returns {undefined} - add property define in koModel
 */
function extendProperties(koModel, schema, keys, model, customValidators, i18n) {
  if (!_.isEmpty(schema) && !_.isEmpty(keys)) {
    const ret = getObservables(schema, keys, model, customValidators, i18n);

    _.extend(koModel, CustomConstraints(ret, schema), {
      toObject: _.partial(toObject, koModel, keys),
      validate: _.partial(validate, koModel, keys),
      hasChange: _.partial(hasChange, koModel, model, keys),
      setModel: _.partial(setModel, koModel, keys),
    });
  }

  _.extend(koModel, { errors: ko.observableArray() });

  _.extend(koModel.errors, {
    add: _.partial(addErrors, koModel, keys, i18n),
    clear: _.partial(clearErrors, koModel, keys),
  });
}

/**
 * define property in object with validation and errors
 * @param {KnockoutViewModel} instance - Object to extend property
 * @param {JsonSchema} schema - an object which contains property 'properties',
 * the 'properties' is an Object contains properties and correspond define
 * @param {string[]} keys - list of peroerty to extend in this module
 * @param {Object} model - the model to initialize the view model
 * @param {CustomerValidators} customValidators - custom validators for each property
 * @param {I18n} i18n - used to get value for each validator
 * @returns {undefined} - add property define in instance
 */
export function KoSchema(instance, schema, keys, model, customValidators, i18n) {
  extendProperties(instance, schema, keys, model, customValidators, i18n);
}
